import {
	AmbientLight,
	Mesh,
	MeshStandardMaterial,
	Scene,
	SphereGeometry
} from 'three';

import {
	AmbientLightHelperObjects,
	getQualitySegments,
	LIGHT_HELPER_DEFAULTS,
	RenderQuality
} from './light-helper.types';

/**
 * Creates visualization objects for an AmbientLight (sphere only)
 *
 * AmbientLight has no position in the traditional sense (it lights everything equally),
 * so we place the sphere at origin or a specified position.
 *
 * @param light The AmbientLight to visualize
 * @param scene Scene to add objects to
 * @param sphereRadius Radius of the sphere
 * @param visible Initial visibility state
 * @param quality Render quality 0-10
 * @returns AmbientLight helper objects
 */
export const createAmbientLightVisualization = (
	light: AmbientLight,
	scene: Scene,
	sphereRadius: number = LIGHT_HELPER_DEFAULTS.CONE_RADIUS,
	visible: boolean = false,
	quality: RenderQuality = LIGHT_HELPER_DEFAULTS.QUALITY
): AmbientLightHelperObjects => {
	const isLightOn = light.visible && light.intensity > 0;
	const segments = getQualitySegments(quality);

	// Create sphere - AmbientLight doesn't have a meaningful position,
	// but we use its position property if set, otherwise origin
	const sphereGeometry = new SphereGeometry(sphereRadius, segments.radial, segments.radial);
	const sphereColor = isLightOn
		? LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_ON
		: LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF;
	const sphereMaterial = new MeshStandardMaterial({ color: sphereColor, wireframe: false }); // Interface objects never wireframe
	const sphere = new Mesh(sphereGeometry, sphereMaterial);
	sphere.name = `ambientLightSphere_${light.uuid}`;
	sphere.position.copy(light.position);
	sphere.visible = visible;
	// Mark as interface object to exclude from wireframe mode
	sphere.userData.isInterface = true;
	sphere.userData.type = 'helper';
	scene.add(sphere);

	return { sphere };
};

/**
 * Updates AmbientLight visualization objects
 */
export const updateAmbientLightVisualization = (
	light: AmbientLight,
	objects: AmbientLightHelperObjects,
	visible: boolean
): void => {
	const isLightOn = light.visible && light.intensity > 0;

	// Update sphere position
	objects.sphere.position.copy(light.position);
	objects.sphere.visible = visible;

	// Update sphere color
	const sphereMaterial = objects.sphere.material as MeshStandardMaterial;
	sphereMaterial.color.setHex(
		isLightOn ? LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_ON : LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF
	);
};

/**
 * Disposes AmbientLight visualization objects
 */
export const disposeAmbientLightVisualization = (
	objects: AmbientLightHelperObjects,
	scene: Scene
): void => {
	scene.remove(objects.sphere);
	objects.sphere.geometry.dispose();
	(objects.sphere.material as MeshStandardMaterial).dispose();
};

